/** Helpers for PatientDashboardV1 — display existing fields only, no clinical scoring. */

export const SCALE_LABELS = {
  barthel: "Barthel",
  lawton: "Lawton",
  katz: "Katz",
  mmse: "MMSE",
  pfeiffer: "Pfeiffer",
  yesavage: "Yesavage GDS",
  mna_sf: "MNA-SF",
  mna: "MNA",
  morse: "Morse",
  tinetti: "Tinetti",
  frail: "FRAIL",
  cfs: "CFS",
  charlson: "Charlson",
  gijon: "Gijón",
  eva_dolor: "EVA Dolor",
  norton: "Norton",
  gad7: "GAD-7",
  sarc_f: "SARC-F",
  tug: "TUG",
  cam: "CAM",
  mini_cog: "Mini-Cog",
  moca: "MoCA",
  polifarmacia_simple: "Polifarmacia simple",
  phq9: "PHQ-9",
  sppb: "SPPB",
  zarit: "Zarit",
};

const VITAL_UNITS = {
  hr: "lpm",
  rr: "rpm",
  temp: "°C",
  spo2: "%",
  weight: "kg",
  height: "cm",
  bmi: "kg/m²",
  glucose: "mg/dL",
  waist_circ: "cm",
  calf_circ: "cm",
  arm_circ: "cm",
  weight_loss: "kg",
};

const VITAL_ORDER = [
  "bp",
  "hr",
  "rr",
  "temp",
  "spo2",
  "weight",
  "height",
  "bmi",
  "glucose",
  "waist_circ",
  "calf_circ",
  "arm_circ",
  "weight_loss",
  "notes",
];

const FRIENDLY_RECORD_KEYS = [
  "numero_expediente",
  "expediente",
  "codigo_expediente",
  "record_number",
];

const MONGO_ID_RE = /^[a-f0-9]{24}$/i;

export function patientFullName(patient) {
  if (!patient) return "";
  return `${patient.nombres || ""} ${patient.apellidos || ""}`.trim();
}

export function patientInitials(patient) {
  const n = String(patient?.nombres || "").trim();
  const a = String(patient?.apellidos || "").trim();
  const ni = n ? n[0] : "";
  const ai = a ? a[0] : "";
  const initials = `${ni}${ai}`.toUpperCase();
  if (initials) return initials;
  const fallback = patientFullName(patient);
  return fallback ? fallback.slice(0, 2).toUpperCase() : "—";
}

export function formatDisplayDate(raw) {
  if (!raw) return null;
  const t = Date.parse(raw);
  if (Number.isNaN(t)) return String(raw);
  return new Date(t).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function consultationDateRaw(c) {
  return c?.fecha_consulta || c?.created_at || null;
}

export function sortConsultationsNewestFirst(list) {
  return [...(list || [])].sort((a, b) => {
    const ta = Date.parse(consultationDateRaw(a) || "") || 0;
    const tb = Date.parse(consultationDateRaw(b) || "") || 0;
    return tb - ta;
  });
}

function isBlank(v) {
  return v == null || String(v).trim() === "";
}

function asTextList(value) {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "string") return item.trim();
        if (typeof item === "object") {
          return String(item.nombre || item.name || item.label || item.texto || "").trim();
        }
        return String(item).trim();
      })
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeForExactCompare(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function pushUnique(list, value) {
  const original = String(value || "").trim();
  if (!original) return;
  const key = normalizeForExactCompare(original);
  if (list.some((x) => normalizeForExactCompare(x) === key)) return;
  list.push(original);
}

export function friendlyRecordNumber(patient) {
  if (!patient || typeof patient !== "object") return null;
  for (const key of FRIENDLY_RECORD_KEYS) {
    const raw = patient[key];
    if (isBlank(raw)) continue;
    const text = String(raw).trim();
    if (MONGO_ID_RE.test(text)) continue;
    if (text === String(patient.id || "") || text === String(patient._id || "")) continue;
    return text;
  }
  return null;
}

export function sexLabel(patient) {
  const v = patient?.genero || patient?.sexo || "";
  return String(v).trim();
}

export function hasScaleEntries(escalas) {
  if (!escalas || typeof escalas !== "object") return false;
  return Object.keys(escalas).some((k) => escalas[k] != null);
}

export function listScaleEntries(escalas) {
  if (!escalas || typeof escalas !== "object") return [];
  return Object.entries(escalas)
    .map(([id, raw]) => {
      if (raw == null) return null;
      const value =
        typeof raw === "object"
          ? raw.value ?? raw.puntaje ?? raw.score ?? null
          : raw;
      const interpretacion =
        typeof raw === "object" && raw.interpretacion != null && String(raw.interpretacion).trim()
          ? String(raw.interpretacion).trim()
          : null;
      const emptyValue = isBlank(value);
      if (emptyValue && !interpretacion) return null;
      return {
        id,
        label: SCALE_LABELS[id] || id,
        value: emptyValue ? null : value,
        interpretacion,
      };
    })
    .filter(Boolean);
}

export function findLatestVgiConsultation(consultations) {
  for (const c of consultations || []) {
    if (listScaleEntries(c.escalas).length) return c;
  }
  return null;
}

export function listScalesByConsultation(consultations) {
  const groups = [];
  for (const c of consultations || []) {
    const scales = listScaleEntries(c.escalas);
    if (!scales.length) continue;
    groups.push({
      consultationId: c.id,
      dateRaw: consultationDateRaw(c),
      dateLabel: formatDisplayDate(consultationDateRaw(c)),
      scales,
    });
  }
  return groups;
}

export function formatVitalValue(value, unit) {
  const text = String(value).trim();
  if (!unit) return text;
  if (text.toLowerCase().includes(unit.toLowerCase().replace("²", "2"))) return text;
  return `${text} ${unit}`;
}

export function listVitalRows(vitals) {
  if (!vitals || typeof vitals !== "object") return [];
  const rows = [];
  const sbp = isBlank(vitals.sbp) ? null : String(vitals.sbp).trim();
  const dbp = isBlank(vitals.dbp) ? null : String(vitals.dbp).trim();
  if (sbp || dbp) {
    const pair = `${sbp || "—"}/${dbp || "—"} mmHg`;
    rows.push({ key: "bp", label: "Presión arterial", value: pair });
  }

  const rest = {
    hr: "FC",
    rr: "FR",
    temp: "Temperatura",
    spo2: "SpO₂",
    weight: "Peso",
    height: "Talla",
    bmi: "IMC",
    glucose: "Glucosa",
    waist_circ: "Cintura",
    calf_circ: "Pantorrilla",
    arm_circ: "Brazo",
    weight_loss: "Pérdida de peso",
    notes: "Notas",
  };

  for (const key of VITAL_ORDER) {
    if (key === "bp") continue;
    const label = rest[key];
    if (!label) continue;
    const raw = vitals[key];
    if (isBlank(raw)) continue;
    rows.push({
      key,
      label,
      value: formatVitalValue(raw, VITAL_UNITS[key]),
    });
  }
  return rows;
}

export function parseMedication(m) {
  if (m == null) return null;
  if (typeof m === "string") {
    const nombre = m.trim();
    if (!nombre) return null;
    return { nombre, dosis: "", frecuencia: "" };
  }
  if (typeof m === "object") {
    const nombre = String(m.nombre || m.nombre_medicamento || "").trim();
    const dosis = String(m.dosis || "").trim();
    const frecuencia = String(m.frecuencia || "").trim();
    if (!nombre && !dosis && !frecuencia) return null;
    return { nombre: nombre || "Medicamento", dosis, frecuencia };
  }
  return null;
}

export function collectRegisteredMedications(patient, latestConsultation) {
  const fromPatient = (patient?.medicamentos_actuales || []).map(parseMedication).filter(Boolean);
  if (fromPatient.length) return { source: "paciente", items: fromPatient };

  const fromConsultActuales = (latestConsultation?.medicamentos_actuales || [])
    .map(parseMedication)
    .filter(Boolean);
  if (fromConsultActuales.length) {
    return { source: "consulta_actuales", items: fromConsultActuales };
  }

  const fromIndicados = (latestConsultation?.medicamentos_indicados || [])
    .map(parseMedication)
    .filter(Boolean);
  if (fromIndicados.length) {
    return { source: "consulta_indicados", items: fromIndicados };
  }

  return { source: null, items: [] };
}

export function collectStoredAlerts(consultation) {
  const raw = consultation?.clinical_alerts;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .map((a) => {
      if (a == null) return "";
      if (typeof a === "string") return a.trim();
      if (typeof a === "object") {
        return String(a.mensaje || a.message || a.texto || a.title || a.titulo || "").trim();
      }
      return String(a).trim();
    })
    .filter(Boolean);
}

export function collectClinicalSummary(patient, latestConsultation) {
  const alergias = [];
  const alergiasPaciente = asTextList(patient?.alergias);
  if (alergiasPaciente.length) {
    alergiasPaciente.forEach((x) => pushUnique(alergias, x));
  } else {
    asTextList(latestConsultation?.antecedentes?.alergias).forEach((x) => pushUnique(alergias, x));
  }

  const diagnosticos = [];
  pushUnique(diagnosticos, latestConsultation?.diagnostico);
  asTextList(latestConsultation?.suggested_diagnoses).forEach((x) => pushUnique(diagnosticos, x));

  const antecedentes = [];
  pushUnique(antecedentes, patient?.ultimo_antecedente_patologico);
  pushUnique(antecedentes, patient?.latest_antecedentes?.patologicos);
  asTextList(patient?.enfermedades_cronicas).forEach((x) => pushUnique(antecedentes, x));
  asTextList(patient?.chronic_conditions).forEach((x) => pushUnique(antecedentes, x));
  pushUnique(antecedentes, latestConsultation?.antecedentes?.patologicos);

  const sindromes = [];
  pushUnique(sindromes, latestConsultation?.sindrome_geriatrico);

  return {
    alergias,
    diagnosticos,
    antecedentes,
    sindromes,
    hasAny:
      alergias.length > 0 ||
      diagnosticos.length > 0 ||
      antecedentes.length > 0 ||
      sindromes.length > 0,
  };
}

export function summarizeText(raw, max = 90) {
  const t = String(raw || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  if (t.length <= max) return t;
  return `${t.slice(0, max).trim()}…`;
}
